import type { EnvironmentClient } from "@core/domain/infrastructures/env/environment-client";
import type { HttpRequestConfig } from "@core/domain/infrastructures/http/http-request-config";
import { coreTypes } from "@core/root/core-types.di";
import type { AxiosRequestConfig, RawAxiosRequestHeaders } from "axios";
import { inject, injectable } from "inversify";

/**
 * AxiosRequestConfig extended with our own per-request metadata. Axios keeps
 * unknown config properties, so they are readable back from `error.config`
 * inside the response interceptors.
 */
export interface IotAxiosRequestConfig extends AxiosRequestConfig {
    emitUnauthorizedEvent?: boolean;
}

@injectable()
export class AxiosHttpConfig {
    constructor(
        @inject(coreTypes.infrastructure.EnvironmentClient)
        private readonly env: EnvironmentClient,
    ) {}

    get apiUrl(): string {
        return this.env.apiServerUrl + this.env.apiBaseUrl;
    }

    fromHttpConfig(
        httpConfig: HttpRequestConfig | undefined,
    ): AxiosRequestConfig | undefined {
        const axiosRequestConfig: IotAxiosRequestConfig = {};

        if (httpConfig) {
            if (httpConfig.baseUrl) {
                axiosRequestConfig.baseURL = httpConfig.baseUrl;
            }

            if (httpConfig.headers) {
                axiosRequestConfig.headers = {
                    ...httpConfig.headers,
                    ...(axiosRequestConfig.headers as RawAxiosRequestHeaders),
                };
            }

            if (httpConfig.query) {
                axiosRequestConfig.params = httpConfig.query;
            }

            if (httpConfig.responseType) {
                axiosRequestConfig.responseType = httpConfig.responseType;
            }

            if (httpConfig.emitUnauthorizedEvent !== undefined) {
                axiosRequestConfig.emitUnauthorizedEvent =
                    httpConfig.emitUnauthorizedEvent;
            }
        }

        return axiosRequestConfig;
    }
}
